import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import { CText } from '../common';
import { FiTrendingUp, FiLock, FiArrowRight, FiGrid } from 'react-icons/fi';
import routes from '../../utils/routes';

interface Bucket {
  name: string;
  stockCount: number;
  category: string;
  isLocked?: boolean;
}

interface TrendingBucketsProps {
  subscriptionTier?: 'free' | 'pro' | 'premium';
  onUpgradeClick?: () => void;
}

const TrendingBuckets: React.FC<TrendingBucketsProps> = ({
  subscriptionTier = 'free',
  onUpgradeClick,
}) => {
  const colors = useAppSelector((state) => state.theme.theme);
  const navigate = useNavigate();

  // Sample buckets - 2 free, 1 locked for free users
  const buckets: Bucket[] = [
    {
      name: 'Excellent Results',
      stockCount: 12,
      category: 'functional',
      isLocked: false,
    },
    {
      name: 'Banking Leaders',
      stockCount: 8,
      category: 'sectoral',
      isLocked: false,
    },
    {
      name: 'Value Champions',
      stockCount: 15,
      category: 'functional',
      isLocked: subscriptionTier === 'free',
    },
  ];

  const getCategoryIcon = (category: string) => {
    return FiTrendingUp;
  };

  const getCategoryGradient = (category: string, index: number) => {
    const gradients = [
      'from-emerald-500 to-teal-500',
      'from-blue-500 to-indigo-500',
      'from-purple-500 to-pink-500',
    ];
    return gradients[index % gradients.length];
  };

  const handleBucketClick = (bucket: Bucket) => {
    if (bucket.isLocked) {
      onUpgradeClick?.();
    } else {
      navigate(`${routes.buckets}/${encodeURIComponent(bucket.name)}`);
    }
  };

  return (
    <div className="mb-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center"
            style={{
              background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
            }}
          >
            {React.createElement(FiGrid as any, { size: 16, color: '#fff' })}
          </div>
          <div>
            <CText type="M16" color={colors.textColor} style={{ fontWeight: 600 }}>
              Trending Investment Themes
            </CText>
            <CText type="r12" color={colors.grayScale500}>
              AI-curated portfolios updated daily
            </CText>
          </div>
        </div>
        <button
          onClick={() => navigate(routes.buckets)}
          className="flex items-center gap-1 text-sm font-medium transition-all duration-200 hover:translate-x-1"
          style={{ color: colors.primary }}
        >
          See all
          {React.createElement(FiArrowRight as any, { size: 14 })}
        </button>
      </div>

      {/* Bucket Cards - Horizontal Scroll */}
      <div className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 scrollbar-hide">
        {buckets.map((bucket, idx) => {
          const Icon = getCategoryIcon(bucket.category);
          const gradient = getCategoryGradient(bucket.category, idx);

          return (
            <button
              key={bucket.name}
              onClick={() => handleBucketClick(bucket)}
              className="flex-shrink-0 w-40 p-4 rounded-2xl text-left transition-all duration-200 hover:-translate-y-1 active:scale-95 card-shadow relative overflow-hidden"
              style={{
                backgroundColor: bucket.isLocked
                  ? colors.grayScale400 + '20'
                  : colors.cardBackground || colors.inputBackground,
                border: `1px solid ${bucket.isLocked ? colors.grayScale400 : colors.borderColor}`,
              }}
            >
              {/* Locked Overlay */}
              {bucket.isLocked && (
                <div
                  className="absolute inset-0 flex flex-col items-center justify-center backdrop-blur-sm z-10"
                  style={{ backgroundColor: colors.backgroundColor + 'E0' }}
                >
                  <div
                    className="w-12 h-12 rounded-full flex items-center justify-center mb-2"
                    style={{ backgroundColor: colors.grayScale400 + '40' }}
                  >
                    {React.createElement(FiLock as any, { size: 24, color: colors.grayScale500 })}
                  </div>
                  <CText type="M12" color={colors.textColor} style={{ fontWeight: 600 }}>
                    Pro Only
                  </CText>
                  <div
                    className="mt-2 px-3 py-1 rounded-full text-xs font-semibold"
                    style={{
                      background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)`,
                      color: '#fff',
                    }}
                  >
                    Unlock
                  </div>
                </div>
              )}

              {/* Icon */}
              <div
                className={`w-10 h-10 rounded-xl flex items-center justify-center mb-3 bg-gradient-to-br ${gradient}`}
              >
                {React.createElement(Icon as any, { size: 20, color: '#fff' })}
              </div>

              {/* Bucket Name */}
              <CText
                type="M14"
                color={colors.textColor}
                style={{ fontWeight: 600, marginBottom: 4 }}
              >
                {bucket.name}
              </CText>

              {/* Stock Count */}
              <CText type="r12" color={colors.grayScale500}>
                {bucket.stockCount} stocks
              </CText>

              {/* View Button */}
              {!bucket.isLocked && (
                <div
                  className="mt-3 py-1.5 rounded-lg text-center text-xs font-medium"
                  style={{
                    backgroundColor: colors.primary + '15',
                    color: colors.primary,
                  }}
                >
                  View Free
                </div>
              )}
            </button>
          );
        })}
      </div>

      {/* Free vs Pro Info */}
      {subscriptionTier === 'free' && (
        <div
          className="mt-4 p-3 rounded-xl flex items-center justify-between"
          style={{
            backgroundColor: colors.backgroundColor,
            border: `1px solid ${colors.borderColor}`,
          }}
        >
          <CText type="r12" color={colors.grayScale500}>
            <span style={{ fontWeight: 600, color: colors.textColor }}>Free:</span> 2 buckets |{' '}
            <span style={{ fontWeight: 600, color: colors.primary }}>Pro:</span> All 15+ buckets
          </CText>
          <button
            onClick={onUpgradeClick}
            className="text-xs font-semibold"
            style={{ color: colors.primary }}
          >
            Upgrade →
          </button>
        </div>
      )}
    </div>
  );
};

export default TrendingBuckets;
